import { apiClient, apiFileUploadClient, v3Client } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CommonFileUploadFiles } from '@components/FileUpload/CommonFileUpload';
import { ToastType } from '@components/Toast';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';
import { produce } from 'solid-js/store';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { FeatureFlags } from '@constants/featureFlags';

import { setUserDetailStore, userDetailStore } from './store';
import {
  ATGUserDataType,
  StoreGoogleCredentialType,
  UserDetailsType,
  UserPastChargesType,
  UserPendingChargesType,
} from './types';

const isPortRemainingV3ApisEnabled = isFeatureFlagEnabled(
  FeatureFlags.PortRemainingV3Apis,
);

// ts-unused-exports:disable-next-line
export const getUserDetailsApi = async (id: number) => {
  try {
    const response: V4ApiResponse<UserDetailsType> = await apiClient.get(
      `User/details/${id}`,
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Unable to fetch user details');
    handleToast(ToastType.Error, error.message);
  }
};

// ts-unused-exports:disable-next-line
export const fetchUserDetailDocumentsApi = async (id: string) => {
  try {
    const response: V4ApiResponse<CommonFileUploadFiles[]> =
      await apiClient.get(`User/document/${id}`);
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to fetch user documents list.');
    handleToast(ToastType.Error, error.message);
  }
};

export const uploadUserDetailDocumentApi = async (
  userId: number,
  file: File,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .userDetails.documentsUpload,
) => {
  try {
    const formData = new FormData();
    formData.append('Files', file);
    formData.append('DocumentEntityType', '7');
    formData.append('EntityId', userId.toString());
    const res: V4ApiResponse<{
      results: { documentId: number; name: string; url: string }[];
    }> = await apiFileUploadClient.post(
      'User/Document',
      formData,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    if (res.isSuccess) {
      return res.value.results[0];
    }
  } catch (e: unknown) {
    const error = createError(e, 'Document failed to upload');
    handleToast(ToastType.Error, error.message);
  }
};

export const deleteUserDetailDocumentApi = async (
  payload: {
    File: string;
    Id: number;
  },
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .userDetails.documentsDelete,
) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.post(
      'User/DeleteFile',
      payload,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    if (response.isSuccess) {
      return response.isSuccess;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to delete user document');
    handleToast(ToastType.Error, error.message);
  }
};

export const getATGUserDataApi = async () => {
  try {
    if (isPortRemainingV3ApisEnabled()) {
      const response: V4ApiResponse<ATGUserDataType> =
        await apiClient.get('/Data');
      if (!response.isSuccess) {
        handleToast(
          ToastType.Error,
          response.errors[0]?.message ??
            'Failed to get roles and permissions info',
        );
        return;
      }
      return response.value;
    }

    const response: unknown = await v3Client.get('/Data');
    return response as ATGUserDataType;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to get roles and permissions info');
    handleToast(ToastType.Error, error.message);
  }
};

/**
 * Extracts the relative path starting from the first occurrence of "User/" (case-insensitive) in the given URL.
 * @param url - The full URL as a string.
 * @returns The relative path starting from "User/" if found; otherwise, an empty string.
 * Handles URLs with query parameters or fragments. If the input is not a valid string, returns an empty string.
 */
export const extractRelativePath = (url: string) => {
  if (typeof url !== 'string' || url.trim() === '') {
    return '';
  }
  const match = url.match(/User\/.*$/i); // Case-insensitive match
  return match ? match[0] : '';
};

export const userProfilePhotoUploadApi = async (
  file: FormData,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .userDetails.profilePhotoUpload,
) => {
  try {
    const response: V4ApiResponse<{ photo: string; thumbnail: string }> =
      await apiFileUploadClient.post(
        `/User/UploadPhoto/${userDetailStore.userDetails.id}`,
        file,
        buildChangeTrackingRequestConfig([changeTrackingSource]),
      );
    if (response.isSuccess) {
      const { photo, thumbnail } = response.value;

      const relativePhoto = extractRelativePath(photo);
      const relativeThumbnail = extractRelativePath(thumbnail);

      setUserDetailStore(
        produce((draft) => {
          draft.userDetails.picture = relativePhoto;
          draft.userDetails.thumbnail = relativeThumbnail;
        }),
      );

      handleToast(
        ToastType.Success,
        'Profile Photo Updated. You look amazing.',
      );
      return true;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to upload image.');
    handleToast(ToastType.Error, error.message);
  }
};

// ts-unused-exports:disable-next-line
export const fetchPendingChargesApi = async (id: string) => {
  try {
    const response: V4ApiResponse<UserPendingChargesType[]> =
      await apiClient.get(`/User/Adjustments/${id}`);
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to get pending charges data.');
    handleToast(ToastType.Error, error.message);
  }
};

export async function fetchPastChargesApi(id: string) {
  try {
    const response: V4ApiResponse<UserPastChargesType[]> = await apiClient.get(
      `user/useradjustments/${id}`,
    );
    if ('value' in response) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to fetch past charges data.');
    handleToast(ToastType.Error, error.message);
  }
}

// ts-unused-exports:disable-next-line
export const saveUserAdjustmentsApi = async (
  userId: number,
  payload: Record<string, unknown>[],
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .userDetails.pendingChargesSave,
) => {
  try {
    const url = `/User/Adjustments/${userId}`;
    const response: V4ApiResponse<UserPendingChargesType[]> =
      await apiClient.post(
        url,
        {
          adjustments: payload,
        },
        buildChangeTrackingRequestConfig([changeTrackingSource]),
      );
    return response;
  } catch (e) {
    const error = createError(e, 'Failed to save user adustments');
    handleToast(ToastType.Error, error.message);
  }
};

export const saveUserDetailsApi = async (
  data: UserDetailsType,
  changedSources: readonly ChangeTrackingSource[] = [],
) => {
  try {
    const trackingConfig = buildChangeTrackingRequestConfig(changedSources);

    const response: V4ApiResponse<UserDetailsType | boolean | null> =
      await apiClient.put('/User', data, trackingConfig);
    if (!response.isSuccess) {
      handleToast(
        ToastType.Error,
        response.errors[0]?.message ?? 'Information failed to save',
      );
      return false;
    }

    return response.value !== false;
  } catch (e) {
    const error = createError(e, 'Information failed to save');
    handleToast(ToastType.Error, error.message);
  }
};

// ts-unused-exports:disable-next-line
export const storeGoogleCredentialApi = async (
  payload: StoreGoogleCredentialType,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .userDetails.storeGoogleCredential,
) => {
  const url = `/User/StoreGoogleCredential/${payload.userId}`;
  try {
    // TODO: Need to verify for redirect uri
    const response: {
      errors: {
        code: string;
        message: string;
      }[];
      isFailure: boolean;
      isSuccess: boolean;
    } = await apiClient.post(
      url,
      payload,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    if (response.isSuccess) {
      return response.isSuccess;
    }
  } catch (e) {
    const error = createError(e, 'Failed to save google credentials');
    handleToast(ToastType.Error, error.message);
  }
};
